<script setup lang="ts">
import {
  Circle as CircleIcon,
  MapPin as MapPinIcon,
  Pause as PauseIcon,
  Route as RouteIcon,
  Upload as UploadIcon,
} from '@lucide/vue'
import { computed, nextTick, ref, watch, type Component } from 'vue'
import { useRoute } from 'vue-router'

import CreateCollectionIcon from '@/components/icons/CreateCollectionIcon.vue'
import BaseFab from '@/components/ui/BaseFab.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useLibraryStore } from '@/stores/libraryStore'
import { useRecordingStore } from '@/stores/recordingStore'
import { useUiStore } from '@/stores/uiStore'
import { Z_INDEX } from '@/utils/zIndex'

type MenuItem = {
  icon: Component
  key: string
  label: string
  run: () => void | Promise<void>
}

const props = withDefaults(
  defineProps<{
    openDirection?: 'down' | 'up'
    size?: 'desktop' | 'mobile'
  }>(),
  {
    openDirection: 'down',
    size: 'desktop',
  },
)

const route = useRoute()
const libraryStore = useLibraryStore()
const recordingStore = useRecordingStore()
const uiStore = useUiStore()

const isMenuOpen = ref(false)
const buttonRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const dropdownPosition = ref<{ top?: number; bottom?: number; left: number }>({ top: 0, left: 0 })

const hasWorkspaceRoute = computed(
  () => typeof route.params.workspaceId === 'string' && route.params.workspaceId.length > 0,
)
const isCollectionContext = computed(
  () =>
    route.name === 'pinned' ||
    (!uiStore.isInWorkspacesOverview && !uiStore.isAllActivitiesView && hasWorkspaceRoute.value),
)

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node
  if (!menuRef.value?.contains(target) && !dropdownRef.value?.contains(target)) {
    closeMenu()
  }
}

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isMenuOpen.value) closeMenu()
}

useEventListener(document, 'click', handleClickOutside)
useEventListener(document, 'keydown', handleEscape)

const updateDropdownPosition = () => {
  if (!buttonRef.value) return
  const rect = buttonRef.value.getBoundingClientRect()
  const MARGIN = 16
  const menuWidth = dropdownRef.value?.getBoundingClientRect().width ?? 0
  const maxLeft = Math.max(MARGIN, window.innerWidth - menuWidth - MARGIN)
  const left = Math.min(Math.max(MARGIN, rect.left), maxLeft)
  dropdownPosition.value =
    props.openDirection === 'up'
      ? { bottom: window.innerHeight - rect.top + 8, left }
      : { top: rect.bottom + 8, left }
}

watch(
  () => isMenuOpen.value,
  async (open) => {
    if (!open) return
    await nextTick()
    updateDropdownPosition()
  },
)

useEventListener(window, 'resize', () => {
  if (isMenuOpen.value) updateDropdownPosition()
})

const dispatch = (name: string) => {
  window.dispatchEvent(new CustomEvent(name))
  closeMenu()
}

const handleCreateLibraryRoute = async () => {
  closeMenu()
  const tourId = await libraryStore.createEmptyTour()
  if (!tourId) return
  uiStore.isAllActivitiesView = true
  window.dispatchEvent(
    new CustomEvent('open-all-activities', { detail: { preserveMobilePanelState: true } }),
  )
  await nextTick()
  window.dispatchEvent(
    new CustomEvent('start-track-drawing', {
      detail: { tourId, scope: 'library', discardTourOnCancel: true },
    }),
  )
}

const handleImportToLibrary = async () => {
  closeMenu()
  uiStore.isAllActivitiesView = true
  window.dispatchEvent(
    new CustomEvent('open-all-activities', { detail: { preserveMobilePanelState: true } }),
  )
  await nextTick()
  window.dispatchEvent(new CustomEvent('header-action-import-files'))
}

const handleOpenRecording = () => {
  closeMenu()
  recordingStore.openModal()
}

const menuItems = computed<MenuItem[]>(() => [
  ...(isCollectionContext.value
    ? [
        {
          key: 'draw-route',
          label: 'Draw new route',
          icon: RouteIcon,
          run: () => dispatch('workspace-add-draw-route'),
        },
        {
          key: 'mark-poi',
          label: 'Mark point of interest',
          icon: MapPinIcon,
          run: () => dispatch('workspace-add-poi'),
        },
        {
          key: 'import-files',
          label: 'Import GPS files and photos',
          icon: UploadIcon,
          run: () => dispatch('workspace-add-import-files'),
        },
      ]
    : [
        {
          key: 'create-collection',
          label: 'Create new collection',
          icon: CreateCollectionIcon,
          run: () => dispatch('header-action-create-workspace'),
        },
        {
          key: 'draw-route',
          label: 'Draw new route in library',
          icon: RouteIcon,
          run: handleCreateLibraryRoute,
        },
        {
          key: 'import-files',
          label: 'Import GPS files and photos',
          icon: UploadIcon,
          run: handleImportToLibrary,
        },
      ]),
  ...(recordingStore.isSupported
    ? [
        {
          key: 'record-track',
          label: 'Record track',
          icon: CircleIcon,
          run: handleOpenRecording,
        },
      ]
    : []),
])

// Forward BaseFab's root <button> so the menu-positioning code keeps an HTMLElement.
const setFabEl = (inst: unknown) =>
  (buttonRef.value = (inst as { rootEl?: HTMLElement } | null)?.rootEl ?? null)
</script>

<template>
  <div ref="menuRef" class="relative">
    <BaseFab
      v-if="recordingStore.hasRecording"
      :size="props.size === 'mobile' ? 'lg' : 'md'"
      variant="recording"
      :aria-label="
        recordingStore.isPaused ? 'Recording paused — open recorder' : 'Recording — open recorder'
      "
      :title="recordingStore.isPaused ? 'Recording paused' : 'Recording…'"
      @click="recordingStore.openModal()"
    >
      <PauseIcon
        v-if="recordingStore.isPaused"
        :class="props.size === 'mobile' ? 'h-[22px] w-[22px]' : 'h-[18px] w-[18px]'"
      />
      <span v-else class="relative flex h-3 w-3" aria-hidden="true">
        <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/75" />
        <span class="relative inline-flex h-3 w-3 rounded-full bg-white" />
      </span>
    </BaseFab>
    <BaseFab
      v-else
      :ref="setFabEl"
      :size="props.size === 'mobile' ? 'lg' : 'md'"
      aria-label="Create new"
      title="Create new"
      :aria-expanded="isMenuOpen"
      aria-haspopup="true"
      @click="toggleMenu"
    />

    <Teleport to="body">
      <div
        v-if="isMenuOpen"
        ref="dropdownRef"
        class="fixed w-max max-w-[calc(100vw-2rem)] rounded-xl border border-black/[0.06] bg-white/98 p-2 shadow-lg backdrop-blur-sm"
        :style="{
          top: dropdownPosition.top !== undefined ? `${dropdownPosition.top}px` : undefined,
          bottom:
            dropdownPosition.bottom !== undefined ? `${dropdownPosition.bottom}px` : undefined,
          left: `${dropdownPosition.left}px`,
          zIndex: Z_INDEX.HEADER + 10,
        }"
        role="menu"
      >
        <button
          v-for="item in menuItems"
          :key="item.key"
          class="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:cursor-pointer hover:bg-gray-50"
          role="menuitem"
          @click="item.run()"
        >
          <component :is="item.icon" class="h-4 w-4 shrink-0 text-gray-500" />
          <span class="leading-5 whitespace-nowrap">{{ item.label }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>
